// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.

import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext.js';
import { apiClient } from '../services/api-client.js';
import { useLocale } from '../hooks/useLocale.js';
import { useInstalledLanguages } from '../hooks/useInstalledLanguages.js';
import { usePageTitle } from '../hooks/usePageTitle.js';

export function LoginPage() {
  usePageTitle('Login');
  const navigate = useNavigate();
  const { login } = useAuth();
  const { t, locale, setLocale } = useLocale();
  const { languages } = useInstalledLanguages();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaCode, setMfaCode] = useState('');
  const [pendingUser, setPendingUser] = useState<any>(null);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      // If MFA step is active, verify the TOTP code
      if (mfaRequired && pendingUser) {
        await apiClient.verifyMfa({ code: mfaCode });
        login(pendingUser);
        navigate('/admin');
        return;
      }

      // All login flows go through the backend (which sets httpOnly cookie)
      const result = (await apiClient.login({ email, password })) as {
        token: string;
        refresh_token: string;
        user: Record<string, unknown>;
        mfa_required?: boolean;
      };

      // If MFA is required, show the TOTP code input
      if (result.mfa_required) {
        setPendingUser(result.user);
        setMfaRequired(true);
        return;
      }

      login(result.user);
      navigate('/admin');
    } catch (err: any) {
      const msg = err?.response?.error || err?.message || t.login.login_failed;
      if (msg === 'ACCOUNT_LOCKED') {
        setError(t.login.account_locked);
      } else if (msg === 'ACCOUNT_DEACTIVATED') {
        setError(t.login.account_deactivated);
      } else if (mfaRequired) {
        setError(t.login.invalid_mfa);
      } else if (err?.code === 'FORBIDDEN' || err?.status === 403) {
        // Session/CSRF issue — prompt user to reload rather than show misleading credentials error
        setError(
          t.login.session_expired ??
            'Your session has expired. Please reload the page and try again.',
        );
      } else {
        setError(t.login.invalid_credentials);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <img
        src="/logo_compliance.png"
        alt="AesirX"
        className="login-logo"
        style={{ height: 100, width: 'auto' }}
      />
      <div className="login-container">
        <h1>{t.login.title}</h1>
        <p className="login-subtitle">{t.login.subtitle}</p>
        {error && (
          <div className="login-error" role="alert" id="login-error">
            {error}
          </div>
        )}
        <form onSubmit={handleSubmit} className="dm-form" aria-label="Login form">
          {!mfaRequired ? (
            <>
              <div className="form-group">
                <label htmlFor="email">{t.login.email}</label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="user@example.com"
                  required
                  autoFocus
                  aria-required="true"
                  aria-invalid={!!error}
                  aria-describedby={error ? 'login-error' : undefined}
                />
              </div>
              <div className="form-group">
                <label htmlFor="password">{t.login.password}</label>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  aria-required="true"
                  aria-invalid={!!error}
                  aria-describedby={error ? 'login-error' : undefined}
                />
              </div>
            </>
          ) : (
            <div className="form-group">
              <label htmlFor="mfaCode">{t.login.mfa_code}</label>
              <input
                id="mfaCode"
                type="text"
                inputMode="numeric"
                pattern="[0-9]{6}"
                maxLength={6}
                value={mfaCode}
                onChange={(e) => setMfaCode(e.target.value)}
                placeholder={t.login.mfa_placeholder}
                required
                autoFocus
                aria-required="true"
                aria-invalid={!!error}
                aria-describedby={error ? 'login-error' : undefined}
              />
              <small>{t.login.mfa_instruction}</small>
            </div>
          )}
          <button type="submit" className="btn btn-primary login-btn" disabled={loading}>
            {loading
              ? mfaRequired
                ? t.login.verifying
                : t.login.signing_in
              : mfaRequired
                ? t.login.verify
                : t.login.sign_in}
          </button>
        </form>
        <div className="login-links" style={{ marginTop: '1rem', textAlign: 'center' }}>
          <a
            href="/forgot-password"
            onClick={(e) => {
              e.preventDefault();
              navigate('/forgot-password');
            }}
          >
            {t.login.forgot_password}
          </a>
        </div>
      </div>
      <div className="login-language-selector">
        <select value={locale} onChange={(e) => setLocale(e.target.value)} aria-label="Language">
          {languages.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}
